import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, CheckCircle2, Calendar } from 'lucide-react'
import { submitLeadB2B } from '@/services/leadService'
import { trackDemoSubmit } from '@/lib/analytics'
import { EXTERNAL_LINKS, INTERNAL_LINKS } from '@/constants/links'
import type { LandingColors } from '@/constants/landing-content/types'

interface DemoFormData {
  email: string
  firstname: string
  phone: string
  company: string
  city: string
}

interface DemoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  colors: LandingColors
  source?: string
  companyLabel?: string
  title?: string
  subtitle?: string
}

export function DemoModal({
  open,
  onOpenChange,
  colors,
  source = 'demo_hero',
  companyLabel = 'Nombre de tu escuela',
  title = 'Solicita tu demo personalizada',
  subtitle = 'Te mostraremos cómo Jamsom Music puede ayudarte a gestionar tu escuela de música.',
}: DemoModalProps) {
  const [formData, setFormData] = useState<Partial<DemoFormData>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [consentMarketing, setConsentMarketing] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.firstname || !formData.company || !formData.city || !consentMarketing) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage('')

    const result = await submitLeadB2B({
      email: formData.email,
      firstname: formData.firstname,
      phone: formData.phone,
      company: formData.company,
      city: formData.city,
      source,
      marketing_consent: consentMarketing,
    })

    setIsSubmitting(false)

    if (result.success) {
      trackDemoSubmit(source)
      setSubmitState('success')
      // Abrir Calendly en nueva pestaña después de 1.5 segundos
      setTimeout(() => {
        window.open(EXTERNAL_LINKS.calendly, '_blank', 'noopener,noreferrer')
        // Cerrar modal después de abrir Calendly
        setTimeout(() => {
          onOpenChange(false)
          // Reset state after close
          setTimeout(() => {
            setSubmitState('idle')
            setFormData({})
            setConsentMarketing(false)
          }, 300)
        }, 1000)
      }, 1500)
    } else {
      setSubmitState('error')
      setErrorMessage(result.error || 'Error desconocido')
    }
  }

  const isFormValid = formData.email && formData.firstname && formData.company && formData.city && consentMarketing

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-landing max-h-[90dvh] overflow-y-auto">
        <AnimatePresence mode="wait">
          {submitState === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `${colors.primary}15` }}
              >
                <CheckCircle2 className="w-8 h-8" style={{ color: colors.primary }} />
              </motion.div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 font-landing">
                ¡Perfecto!
              </h3>
              <p className="text-gray-600">
                Abriendo Calendly para que elijas tu horario...
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-2xl font-landing font-bold tracking-tight text-gray-900">
                  {title}
                </DialogTitle>
                <DialogDescription className="text-center text-base text-gray-600">
                  {subtitle}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                {/* Nombre */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Nombre
                  </label>
                  <Input
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.firstname || ''}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <Input
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                {/* Teléfono */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Teléfono <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <Input
                    type="tel"
                    placeholder="+34 600 000 000"
                    value={formData.phone || ''}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="h-11"
                  />
                </div>

                {/* Empresa */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    {companyLabel}
                  </label>
                  <Input
                    type="text"
                    placeholder="Nombre de la empresa o escuela"
                    value={formData.company || ''}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                {/* Ciudad */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Ciudad
                  </label>
                  <Input
                    type="text"
                    placeholder="Madrid, Barcelona..."
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                {/* Consentimiento de marketing (RGPD) — opt-in explícito, requerido */}
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id="demo-consent"
                    checked={consentMarketing}
                    onCheckedChange={(checked) => setConsentMarketing(checked === true)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="demo-consent"
                    className="text-xs text-gray-600 cursor-pointer leading-relaxed"
                  >
                    Acepto que Jamsom Music me contacte y la{' '}
                    <a href={INTERNAL_LINKS.privacidad} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                      Política de privacidad
                    </a>{' '}
                    y los{' '}
                    <a href={INTERNAL_LINKS.terminos} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                      Términos
                    </a>.
                  </label>
                </div>

                {/* Error message */}
                {submitState === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-sm text-red-600 text-center bg-red-50 py-2 px-3 rounded-lg"
                  >
                    {errorMessage}
                  </motion.p>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full h-12 text-base font-semibold rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    backgroundColor: colors.primary,
                    boxShadow: isFormValid ? `0 8px 16px -4px ${colors.primary}40` : 'none',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5 mr-2" />
                      Agendar demo
                    </>
                  )}
                </Button>

                <p className="text-xs text-gray-500 text-center">
                  Elige el horario que mejor te venga. Sin compromiso.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}

export default DemoModal
